import { useState } from "react";
import { SKU } from "@/data/mockData";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { formatCurrency } from "@/lib/utils";
import { 
  Package, 
  Search, 
  MoreHorizontal,
  Edit,
  Trash2,
  Eye,
  AlertTriangle,
  CheckCircle,
  XCircle,
  TrendingUp,
  TrendingDown
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { StockAlert } from "./StockAlert";
import { PriceHistoryChart } from "./PriceHistoryChart";

interface SKUListProps {
  skus: SKU[];
  onEdit: (id: number) => void;
  onView: (id: number) => void;
  onDelete: (id: number) => void;
  isLoading?: boolean;
}

export const SKUList = ({ skus = [], onEdit, onView, onDelete, isLoading }: SKUListProps) => {
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [selectedSKUs, setSelectedSKUs] = useState<number[]>([]);
  const [showPriceChart, setShowPriceChart] = useState<number | null>(null);

  // Sort SKUs with defensive programming
  const sortedSKUs = [...(skus || [])].sort((a, b) => {
    let aValue: any = a[sortBy as keyof SKU];
    let bValue: any = b[sortBy as keyof SKU];
    
    if (sortBy === 'currentStock') {
      aValue = a.currentStock;
      bValue = b.currentStock;
    } else if (sortBy === 'price') {
      aValue = (a.linkedVendors && Array.isArray(a.linkedVendors) && a.linkedVendors[0]?.defaultPrice) || 0;
      bValue = (b.linkedVendors && Array.isArray(b.linkedVendors) && b.linkedVendors[0]?.defaultPrice) || 0;
    }
    
    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }
    
    if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  // Get stock status
  const getStockStatus = (sku: SKU) => {
    if (sku.currentStock <= 0) {
      return { label: 'Out of Stock', variant: 'destructive' as const, icon: XCircle };
    }
    if (sku.currentStock <= sku.reorderLevel) {
      return { label: 'Low Stock', variant: 'destructive' as const, icon: AlertTriangle };
    }
    if (sku.currentStock <= sku.reorderLevel * 1.5) {
      return { label: 'Warning', variant: 'secondary' as const, icon: AlertTriangle };
    }
    return { label: 'In Stock', variant: 'default' as const, icon: CheckCircle };
  };

  // Get price trend - with defensive programming
  const getPriceTrend = (sku: SKU) => {
    // Defensive check for priceHistory
    if (!sku.priceHistory || !Array.isArray(sku.priceHistory) || sku.priceHistory.length < 2) {
      return null;
    }
    
    const latest = sku.priceHistory[sku.priceHistory.length - 1];
    const previous = sku.priceHistory[sku.priceHistory.length - 2];
    
    // Additional safety checks
    if (!latest || !previous || typeof latest.price !== 'number' || typeof previous.price !== 'number') {
      return null;
    }
    
    if (latest.price > previous.price) {
      return { trend: 'up', percentage: ((latest.price - previous.price) / previous.price * 100).toFixed(1) };
    } else if (latest.price < previous.price) {
      return { trend: 'down', percentage: ((previous.price - latest.price) / previous.price * 100).toFixed(1) };
    }
    return { trend: 'stable', percentage: '0' };
  };

  // Handle bulk selection
  const handleSelectAll = () => {
    if (selectedSKUs.length === skus.length) {
      setSelectedSKUs([]);
    } else {
      setSelectedSKUs(skus.map(sku => sku.id));
    }
  };

  const handleSelectSKU = (id: number) => {
    setSelectedSKUs(prev => 
      prev.includes(id) 
        ? prev.filter(skuId => skuId !== id)
        : [...prev, id]
    );
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Loading SKUs...</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center space-x-4">
                <Skeleton className="h-12 w-12 rounded" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-[250px]" />
                  <Skeleton className="h-4 w-[200px]" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Empty state
  if (skus.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            SKU Inventory (0 items)
          </CardTitle>
          <CardDescription>
            Manage your inventory SKUs, stock levels, and vendor relationships
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Package className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-semibold text-foreground mb-2">No SKUs Found</h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm || selectedCategory !== 'all' || statusFilter !== 'all' 
                ? 'No SKUs match your current filters. Try adjusting your search criteria.'
                : 'No SKUs available. Create your first SKU to get started.'
              }
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                SKU Inventory ({skus.length} items)
              </CardTitle>
              <CardDescription>
                Manage your inventory SKUs, stock levels, and vendor relationships
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Name</SelectItem>
                  <SelectItem value="skuCode">SKU Code</SelectItem>
                  <SelectItem value="category">Category</SelectItem>
                  <SelectItem value="currentStock">Stock Level</SelectItem>
                  <SelectItem value="price">Price</SelectItem>
                  <SelectItem value="createdAt">Date Created</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              >
                {sortOrder === 'asc' ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setViewMode(viewMode === 'table' ? 'grid' : 'table')}
              >
                {viewMode === 'table' ? 'Grid View' : 'Table View'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {selectedSKUs.length > 0 && (
            <div className="mb-4 p-3 bg-primary/5 border border-primary/20 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">
                  {selectedSKUs.length} SKU{selectedSKUs.length > 1 ? 's' : ''} selected
                </span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline">
                    Bulk Edit
                  </Button>
                  <Button size="sm" variant="outline">
                    Export Selected
                  </Button>
                  <Button size="sm" variant="destructive">
                    Delete Selected
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Table View */}
      {viewMode === 'table' && (
        <Card>
          <CardContent className="p-0">
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <input
                        type="checkbox"
                        checked={selectedSKUs.length === skus.length && skus.length > 0}
                        onChange={handleSelectAll}
                        className="rounded border-gray-300"
                      />
                    </TableHead>
                    <TableHead>SKU Code</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Stock</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Vendors</TableHead>
                    <TableHead>Last Updated</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedSKUs.map((sku) => {
                    const stockStatus = getStockStatus(sku);
                    const priceTrend = getPriceTrend(sku);
                    const currentPrice = (sku.linkedVendors && Array.isArray(sku.linkedVendors) && sku.linkedVendors[0]?.defaultPrice) || 0;
                    
                    return (
                      <TableRow key={sku.id} className="hover:bg-muted/50">
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={selectedSKUs.includes(sku.id)}
                            onChange={() => handleSelectSKU(sku.id)}
                            className="rounded border-gray-300"
                          />
                        </TableCell>
                        <TableCell className="font-medium">{sku.skuCode}</TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">{sku.name}</div>
                            <div className="text-sm text-muted-foreground truncate max-w-[200px]">
                              {sku.description}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{sku.category}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{sku.currentStock.toLocaleString()}</span>
                              <span className="text-muted-foreground">{sku.unit}</span>
                            </div>
                            <div className="text-xs text-muted-foreground">
                              Reorder: {sku.reorderLevel}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <stockStatus.icon className="h-4 w-4" />
                            <Badge variant={stockStatus.variant} className="text-xs">
                              {stockStatus.label}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <span className="font-medium">{formatCurrency(currentPrice)}</span>
                            {priceTrend && (
                              <div className="flex items-center gap-1 text-xs">
                                {priceTrend.trend === 'up' && <TrendingUp className="h-3 w-3 text-red-500" />}
                                {priceTrend.trend === 'down' && <TrendingDown className="h-3 w-3 text-green-500" />}
                                <span className={priceTrend.trend === 'up' ? 'text-red-500' : 'text-green-500'}>
                                  {priceTrend.percentage}%
                                </span>
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {sku.linkedVendors && Array.isArray(sku.linkedVendors) && sku.linkedVendors.length > 0 ? (
                              <div>
                                <div className="font-medium">{sku.linkedVendors.length} vendor{sku.linkedVendors.length > 1 ? 's' : ''}</div>
                                <div className="text-xs text-muted-foreground">
                                  {sku.linkedVendors.filter(v => v.isPreferred).length} preferred
                                </div>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">No vendors</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(sku.updatedAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" className="h-8 w-8 p-0">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Actions</DropdownMenuLabel>
                              <DropdownMenuItem onClick={() => onView(sku.id)}>
                                <Eye className="mr-2 h-4 w-4" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => onEdit(sku.id)}>
                                <Edit className="mr-2 h-4 w-4" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setShowPriceChart(sku.id)}>
                                <TrendingUp className="mr-2 h-4 w-4" />
                                Price History
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                className="text-destructive"
                                onClick={() => onDelete(sku.id)}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Grid View */}
      {viewMode === 'grid' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sortedSKUs.map((sku) => {
            const stockStatus = getStockStatus(sku);
            const priceTrend = getPriceTrend(sku);
            const currentPrice = (sku.linkedVendors && Array.isArray(sku.linkedVendors) && sku.linkedVendors[0]?.defaultPrice) || 0;
            
            return (
              <Card key={sku.id} className="hover:shadow-md transition-all">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Package className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-base">{sku.skuCode}</CardTitle>
                        <Badge variant="outline" className="text-xs mt-1">
                          {sku.category}
                        </Badge>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onView(sku.id)}>
                          <Eye className="mr-2 h-4 w-4" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onEdit(sku.id)}>
                          <Edit className="mr-2 h-4 w-4" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setShowPriceChart(sku.id)}>
                          <TrendingUp className="mr-2 h-4 w-4" />
                          Price History
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          className="text-destructive"
                          onClick={() => onDelete(sku.id)}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h3 className="font-medium text-foreground">{sku.name}</h3>
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                      {sku.description}
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Stock Level</p>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="font-semibold">{sku.currentStock.toLocaleString()}</p>
                        <Badge variant={stockStatus.variant} className="text-xs">
                          {stockStatus.label}
                        </Badge>
                      </div>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Unit Price</p>
                      <div className="flex items-center gap-1 mt-1">
                        <p className="font-semibold">{formatCurrency(currentPrice)}</p>
                        {priceTrend && (
                          <div className="flex items-center gap-1 text-xs">
                            {priceTrend.trend === 'up' && <TrendingUp className="h-3 w-3 text-red-500" />}
                            {priceTrend.trend === 'down' && <TrendingDown className="h-3 w-3 text-green-500" />}
                            <span className={priceTrend.trend === 'up' ? 'text-red-500' : 'text-green-500'}>
                              {priceTrend.percentage}%
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="text-sm space-y-1">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Unit:</span>
                      <span>{sku.unit}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Reorder Level:</span>
                      <span>{sku.reorderLevel.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Vendors:</span>
                      <span>{sku.linkedVendors && Array.isArray(sku.linkedVendors) ? sku.linkedVendors.length : 0}</span>
                    </div>
                  </div>

                  <div className="pt-2 border-t border-card-border">
                    <p className="text-xs text-muted-foreground">
                      Last updated: {new Date(sku.updatedAt).toLocaleDateString()}
                    </p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Price History Chart Modal */}
      {showPriceChart && (
        <PriceHistoryChart
          skuId={showPriceChart}
          onClose={() => setShowPriceChart(null)}
        />
      )}
    </div>
  );
};
